/* 
--> handles
Auth
Error handling
JSON parsing
Query params
Reusable request method
*/

import { clearAuthSession, isTokenExpired } from "../auth/auth";

const server = import.meta.env.VITE_API_SERVER || "";

export const ApiError = class extends Error {
    constructor(message, status, data = null) {
        super(message);

        this.name = "ApiError";
        this.status = status;
        this.data = data;
    }
};

const redirectToLogin = () => {
    const redirectPath = window.location.pathname
        + window.location.search
        + window.location.hash;

    clearAuthSession();

    if (window.location.pathname !== "/login") {
        window.location.replace(`/login?redirect=${encodeURIComponent(redirectPath)}`);
    }
};

const getAuthHeaders = () => {
    if (!server || !username || !password) {
        throw new Error("Invalid Authorization");
    }

    const credentials = btoa(`${username}:${password}`);

    return {
        "Content-Type": "application/json",
        Authorization: `Basic ${credentials}`
    }
}

const getTokenHeaders = ({ headers, body, authenticated }) => {
    if (!server) {
        throw new ApiError("Invalid server");
    }

    const requestHeaders = new Headers(headers);
    requestHeaders.set("Accept", "application/json");

    /*
     * Only set Content-Type when we actually have a request body.
     *
     * Important:
     * Do not set application/json on GET requests unnecessarily,
     * because it can trigger a CORS preflight.
     */
    if (body !== null && body !== undefined && !(body instanceof FormData)) {
        requestHeaders.set("Content-Type", "application/json");
    }

    if (authenticated) {
        const token = sessionStorage.getItem("token");
        if (!token) {
            redirectToLogin();
            throw new ApiError("Authentication required", 401);
        }

        if (isTokenExpired(token)) {
            redirectToLogin();
            throw new ApiError("Login required", 401);
        }

        requestHeaders.set("Authorization", `Bearer ${token}`);
    }

    return requestHeaders;
}

export const apiRequest = async ({
    path,
    authenticated = true,
    method = "GET",
    headers = {},
    body = null,
    query = null,

}) => {
    if (!server) {
        throw new ApiError("Invalid server");
    }

    const url = new URL(`${server}${path}`);

    /*
     * Query parameters
     */
    if (query) {
        Object.entries(query).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                url.searchParams.set(key, value);
                // url.searchParams.set("startDate", startIso);
                // url.searchParams.set("endDate", endIso);
            }
        });
    }

    let response;
    try {
        response = await fetch(url.toString(), {
            method,
            headers: getTokenHeaders({ headers, body, authenticated }),
            body:
                body === null || body === undefined
                    ? undefined
                    : body instanceof FormData
                        ? body
                        : JSON.stringify(body),
        });
    } catch (error) {
        throw new ApiError("Unable to connect to server", 0, error);
    }

    //No content response
    if (response.status === 204) return null;

    // Parse response body once
    const contentType = response.headers.get("content-type") || "";
    let responseData = null;

    try {
        if (contentType.includes("application/json")) {
            responseData = await response.json();
        } else {
            responseData = await response.text();
        }
    } catch (error) {
        throw new ApiError("Invalid response from server", response.status, error);
    }

    /*
     * Authenticated request received 401.
     *
     * At this point the JWT is missing, expired,
     * invalid or no longer accepted by the server.
     */
    if (authenticated && response.status === 401) {
        //for authenticated user if error response of 401
        redirectToLogin();
        throw new ApiError("Session expired", 401, responseData);
    }

    /*
     * Other HTTP errors
     */
    if (!response.ok) {
        const message =
            responseData?.error
            || responseData?.message
            || (typeof responseData === "string" && responseData)
            || `Request failed (${response.status})`;

        throw new ApiError(message, response.status, responseData);
    }

    /*
     * Return parsed data, NOT fetch Response.
     */
    return responseData;
}